import { useState } from "react";
import {
  AreaChart, BarChart3, ChartNoAxesCombined, ChartScatter, FlaskConical, Funnel, Grid2x2, Grid3x3, Hash, LayoutDashboard, LineChart, PieChart,
  Plus, Sigma, Table2, X,
} from "lucide-react";
import { GRAIN_LABELS, MAX_COMPARE_PERIODS, type Filter, type FilterOp, type TimeGrain, type VizType } from "@cd/core";
import { GRADIENT_SCHEMES, VIZ_SPECS } from "@cd/viz";
import type { Catalog, CatalogDimension } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input, Label, Select, Switch, Tip } from "../../components/ui/primitives";
import { filterFields } from "./fieldList";
import { FilterValues } from "./FilterValues";
import { metricRefsOf, pivotLimitBy, useBuilder, type Draft } from "./store";

const VIZ_ICONS: Record<VizType, typeof Hash> = {
  kpi: Hash, line: LineChart, area: AreaChart, bar: BarChart3, combo: ChartNoAxesCombined, pie: PieChart,
  heatmap: Grid2x2, scatter: ChartScatter, treemap: LayoutDashboard, funnel: Funnel, table: Table2, pivot: Grid3x3,
};

const DATA_LABELS: Partial<Record<VizType, string>> = { pie: "Slice labels", heatmap: "Values in cells", scatter: "Point names" };

const OPS: { op: FilterOp; label: string; types: string[] }[] = [
  { op: "in", label: "is any of", types: ["string", "number"] },
  { op: "not_in", label: "is none of", types: ["string", "number"] },
  { op: "contains", label: "contains", types: ["string"] },
  { op: "gte", label: "≥", types: ["number"] },
  { op: "lte", label: "≤", types: ["number"] },
  { op: "eq", label: "is", types: ["boolean"] },
  { op: "not_null", label: "is not empty", types: ["string", "number", "boolean"] },
  { op: "is_null", label: "is empty", types: ["string", "number", "boolean"] },
];

const Section = ({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) => (
  <section className="border-b border-border px-4 py-3.5">
    <div className="mb-2 flex items-center justify-between">
      <Label>{title}</Label>
      {action}
    </div>
    {children}
  </section>
);

export function ConfigPane({ catalog, shape, locationIds }: { catalog: Catalog; shape: { dimensions: { type: string }[]; metrics: number }; locationIds?: string[] }) {
  const { draft, update } = useBuilder();
  const metricLabel = (ref: string) => catalog.metrics.find((m) => m.ref === ref)?.label ?? ref;
  const dimOf = (ref: string) => catalog.dimensions.find((d) => d.ref === ref);
  const set = (patch: Partial<Draft>) => update((d) => ({ ...d, ...patch }));
  const setConfig = (k: string, v: unknown) => update((d) => ({ ...d, viz: { ...d.viz, config: { ...d.viz.config, [k]: v } } }));

  const limitBy = pivotLimitBy(draft);
  const sortKeys = [
    ...draft.dimensions.map((d) => ({ key: d.grain ? `${d.ref}:${d.grain}` : d.ref, label: dimOf(d.ref)?.label ?? d.ref })),
    ...draft.metrics.map((m) => (m.kind === "metric" ? { key: m.ref, label: metricLabel(m.ref) } : { key: `formula.${m.id}`, label: m.label })),
  ];

  return (
    <div className="h-full overflow-y-auto">
      <Section title="Visualization">
        <div className="grid grid-cols-4 gap-1.5">
          {VIZ_SPECS.map((v) => {
            const Icon = VIZ_ICONS[v.type];
            const reason = shape.metrics ? v.check(shape as never) : null;
            const active = draft.viz.type === v.type;
            return (
              <Tip key={v.type} content={reason ?? v.description}>
                <button
                  onClick={() => update((d) => ({ ...d, viz: { type: v.type, config: d.viz.type === v.type ? d.viz.config : {} }, vizPinned: true }))}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-lg border px-1 py-2 text-[11px] transition-colors",
                    active ? "border-accent bg-accent-soft text-foreground" : "border-border hover:bg-surface-2 text-muted",
                    reason && !active && "opacity-40",
                  )}
                >
                  <Icon className="size-4" />
                  {v.label}
                </button>
              </Tip>
            );
          })}
        </div>
        <VizOptions draft={draft} setConfig={setConfig} />
      </Section>

      <Section title="Metrics" action={<FormulaButton catalog={catalog} />}>
        {!draft.metrics.length && <p className="text-xs text-subtle">Pick metrics from the left.</p>}
        <ul className="space-y-1">
          {draft.metrics.map((m, i) => (
            <li key={i} className="flex items-center gap-2 rounded-lg bg-surface-2 px-2 py-1.5 text-sm">
              {m.kind === "metric" ? <Sigma className="size-3.5 text-subtle" /> : <FlaskConical className="size-3.5 text-info-fg" />}
              <span className="flex-1 truncate">
                {m.kind === "metric" ? metricLabel(m.ref) : m.label}
                {m.kind === "formula" && <span className="block truncate font-mono text-[10px] text-subtle">{m.expr}</span>}
              </span>
              <button onClick={() => set({ metrics: draft.metrics.filter((_, j) => j !== i) })} className="text-subtle hover:text-foreground" aria-label="Remove">
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
        {draft.metrics.length > 0 && (
          <>
            <Select
              value={draft.averagePer ?? ""}
              onChange={(e) => set({ averagePer: (e.target.value || undefined) as Draft["averagePer"] })}
              className="mt-2 w-full"
              aria-label="Show metrics as"
            >
              <option value="">Show totals</option>
              <option value="day">Show average per day</option>
            </Select>
            {draft.averagePer && (
              <p className="mt-1.5 text-xs text-subtle">
                Each value is divided by the number of days in the range it covers. Grouped by day of week, Mondays are divided
                by the number of Mondays, so weekdays compare fairly even when the range isn't whole weeks.
              </p>
            )}
          </>
        )}
      </Section>

      {draft.dimensions.length > 0 && (
        <Section title="Groupings">
          <ul className="space-y-1">
            {draft.dimensions.map((d, i) => {
              const def = dimOf(d.ref);
              return (
                <li key={d.ref} className="flex items-center gap-2 rounded-lg bg-surface-2 px-2 py-1 text-sm">
                  <span className="flex-1 truncate">{def?.label ?? d.ref}</span>
                  {def?.type === "time" && (
                    <Select
                      value={d.grain}
                      onChange={(e) =>
                        set({ dimensions: draft.dimensions.map((x, j) => (j === i ? { ...x, grain: e.target.value as never } : x)), sort: [] })
                      }
                      className="h-6 text-xs"
                    >
                      {(def.grains ?? []).map((g) => <option key={g} value={g}>by {GRAIN_LABELS[g as TimeGrain] ?? g}</option>)}
                    </Select>
                  )}
                  <button onClick={() => set({ dimensions: draft.dimensions.filter((_, j) => j !== i), sort: [] })} className="text-subtle hover:text-foreground" aria-label="Remove">
                    <X className="size-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      <FiltersSection catalog={catalog} locationIds={locationIds} />

      <Section title="Compare">
        <div className="flex gap-2">
          <Select
            value={draft.compareTo ?? ""}
            onChange={(e) => set({ compareTo: (e.target.value || undefined) as Draft["compareTo"] })}
            className="flex-1"
          >
            <option value="">No comparison</option>
            <option value="previous_period">Preceding period</option>
            <option value="previous_year">Same weekdays last year</option>
          </Select>
          {draft.compareTo && (
            <Select
              value={String(draft.comparePeriods ?? 1)}
              onChange={(e) => set({ comparePeriods: Number(e.target.value) })}
              aria-label="Number of periods to compare"
            >
              {Array.from({ length: MAX_COMPARE_PERIODS }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n === 1 ? "1 back" : `${n} back`}
                </option>
              ))}
            </Select>
          )}
        </div>
        {draft.compareTo && (
          <p className="mt-1.5 text-xs text-subtle">
            {draft.compareTo === "previous_period"
              ? `The same number of days just before the dashboard's range${(draft.comparePeriods ?? 1) > 1 ? `, and the ${(draft.comparePeriods ?? 1) - 1} before that` : ""}. Last 7 days is compared with the 7 days before that.`
              : `The same range 52 weeks earlier${(draft.comparePeriods ?? 1) > 1 ? `, for each of the last ${draft.comparePeriods} years` : ""}, so Mondays are compared with Mondays. For example, Tue Sep 15 2026 is compared with Tue Sep 16 2025.`}
          </p>
        )}
      </Section>

      {draft.dimensions.length > 0 && (
        <Section title="Sort & limit">
          <div className="flex gap-2">
            <Select
              value={draft.sort[0]?.key ?? ""}
              onChange={(e) => set({ sort: e.target.value ? [{ key: e.target.value, dir: draft.sort[0]?.dir ?? "desc" }] : [] })}
              className="min-w-0 flex-1"
            >
              <option value="">Automatic</option>
              {sortKeys.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </Select>
            <Select
              value={draft.sort[0]?.dir ?? "desc"}
              disabled={!draft.sort[0]}
              onChange={(e) => set({ sort: draft.sort[0] ? [{ ...draft.sort[0], dir: e.target.value as "asc" | "desc" }] : [] })}
            >
              <option value="desc">High → low</option>
              <option value="asc">Low → high</option>
            </Select>
          </div>
          <Input
            type="number" min={1} placeholder={limitBy ? "Top rows by total (optional)" : "Row limit (optional)"} className="mt-2"
            value={draft.limit ?? ""}
            onChange={(e) => set({ limit: e.target.value ? Math.max(1, Number(e.target.value)) : undefined })}
          />
          {limitBy && (
            <p className="mt-1.5 text-xs text-subtle">
              Keeps the {draft.limit ?? "N"} {limitBy.dir === "asc" ? "lowest" : "highest"} rows by total {metricLabel(limitBy.by)}, with every column of each.
            </p>
          )}
        </Section>
      )}
    </div>
  );
}

function VizOptions({ draft, setConfig }: { draft: Draft; setConfig: (k: string, v: unknown) => void }) {
  const c = draft.viz.config;
  const t = draft.viz.type;
  const isAxisChart = t === "line" || t === "area" || t === "bar" || t === "combo" || t === "heatmap" || t === "scatter";
  const toggles: { key: string; label: string; def: boolean }[] = [
    ...(t === "bar" ? [{ key: "stacked", label: "Stacked", def: false }, { key: "horizontal", label: "Horizontal bars", def: false }] : []),
    ...(t === "kpi" ? [{ key: "upIsGood", label: "Increase is good", def: true }] : []),
    ...(t === "table" ? [{ key: "totals", label: "Totals row", def: true }] : []),
    ...(t === "table" && !(c.colorScale && c.colorScale !== "none") ? [{ key: "dataBars", label: "Data bars", def: true }] : []),
    ...(isAxisChart || t === "pie"
      ? [{ key: "legend", label: t === "heatmap" ? "Color key" : "Legend", def: true }, { key: "dataLabels", label: DATA_LABELS[t] ?? "Value labels", def: false }]
      : []),
    ...(t === "treemap" ? [{ key: "dataLabels", label: "Values on tiles", def: false }] : []),
  ];
  const colorScale = t === "pivot" || t === "table" || t === "heatmap"
    ? ((c.colorScale as string | undefined) ?? (t === "table" ? "none" : "blue"))
    : null;
  if (!toggles.length && !isAxisChart && !colorScale) return null;
  return (
    <div className="mt-3 space-y-2">
      {isAxisChart && (
        <div className="grid grid-cols-2 gap-2">
          {([["xAxisLabel", "X-axis title"], ["yAxisLabel", "Y-axis title"], ...(t === "combo" ? [["y2AxisLabel", "Right-axis title"]] : [])] as const).map(([key, label]) => (
            <label key={key} className="space-y-1 text-xs text-muted">
              {label}
              <Input
                placeholder={t === "scatter" || t === "combo" ? "Automatic" : "None"} className="h-7 text-xs"
                value={(c[key] as string | undefined) ?? ""}
                onChange={(e) => setConfig(key, e.target.value || undefined)}
              />
            </label>
          ))}
        </div>
      )}
      {colorScale !== null && (
        <>
          <label className="flex items-center justify-between gap-3 text-sm whitespace-nowrap">
            Color scale
            <Select value={colorScale} onChange={(e) => setConfig("colorScale", e.target.value)} className="min-w-0 flex-1 text-xs">
              {GRADIENT_SCHEMES.filter((g) => t !== "heatmap" || g.id !== "none").map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </Select>
          </label>
          {colorScale !== "none" && (
            <label className="flex items-center justify-between text-sm">
              {colorScale === "diverging" ? "Swap colors" : "Reverse (low values darkest)"}
              <Switch checked={c.colorReverse === true} onCheckedChange={(v) => setConfig("colorReverse", v)} />
            </label>
          )}
        </>
      )}
      {toggles.map((o) => (
        <label key={o.key} className="flex items-center justify-between text-sm">
          {o.label}
          <Switch checked={(c[o.key] as boolean | undefined) ?? o.def} onCheckedChange={(v) => setConfig(o.key, v)} />
        </label>
      ))}
    </div>
  );
}

function FormulaButton({ catalog }: { catalog: Catalog }) {
  const { update, draft } = useBuilder();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [expr, setExpr] = useState("");
  const [format, setFormat] = useState("number");
  const refs = metricRefsOf([{ kind: "formula", id: "x", label: "x", expr }]);
  const unknown = refs.filter((r) => !catalog.metrics.some((m) => m.ref === r));
  const valid = label.trim() && expr.trim() && refs.length > 0 && !unknown.length;

  const add = () => {
    const base = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "formula";
    let id = /^[a-z]/.test(base) ? base : `f_${base}`;
    while (draft.metrics.some((m) => m.kind === "formula" && m.id === id)) id += "_2";
    update((d) => ({ ...d, metrics: [...d.metrics, { kind: "formula", id, label: label.trim(), expr: expr.trim(), format: format as never }] }));
    setOpen(false);
    setLabel("");
    setExpr("");
  };

  if (!open)
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Plus className="size-3" /> Formula
      </Button>
    );
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={() => setOpen(false)}>
      <div className="w-[480px] rounded-2xl border border-border bg-surface p-5 shadow-pop" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold">Custom formula</h3>
        <p className="mt-1 text-xs text-muted">
          Combine certified metrics. Each metric is totalled first, then your formula runs on the totals — so
          <span className="font-mono"> sales / orders </span> is a true average check.
        </p>
        <div className="mt-4 space-y-3">
          <Input placeholder="Name, e.g. Average check" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
          <Input placeholder="order_items.net_sales / orders.order_count" value={expr} onChange={(e) => setExpr(e.target.value)} className="font-mono text-xs" />
          <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
            {catalog.metrics.map((m) => (
              <button
                key={m.ref}
                onClick={() => setExpr((x) => `${x}${x && !/[\s(]$/.test(x) ? " " : ""}${m.ref}`)}
                className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] text-muted hover:text-foreground"
              >
                {m.label}
              </button>
            ))}
          </div>
          {unknown.length > 0 && <p className="text-xs text-bad">Unknown metric: {unknown.join(", ")}</p>}
          <Select value={format} onChange={(e) => setFormat(e.target.value)} className="w-full">
            <option value="number">Number</option>
            <option value="currency">Currency</option>
            <option value="ratio">Percent from a ratio (0.25 → 25%)</option>
            <option value="percent">Percent, already × 100 (25 → 25%)</option>
            <option value="integer">Whole number</option>
          </Select>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={!valid} onClick={add}>Add formula</Button>
        </div>
      </div>
    </div>
  );
}

function FiltersSection({ catalog, locationIds }: { catalog: Catalog; locationIds?: string[] }) {
  const { draft, update } = useBuilder();
  // Only fields every selected metric can be filtered by.
  const { allowed, byModel, labels } = filterFields(catalog, draft);
  const setFilters = (filters: Filter[]) => update((d) => ({ ...d, filters }));

  return (
    <Section
      title="Filters"
      action={
        <Tip content={allowed.length ? undefined : "No field can filter the selected metrics — they would double-count."}>
          <span>
            <Button size="sm" variant="ghost" disabled={!allowed.length} onClick={() => setFilters([...draft.filters, { ref: "", op: "in", values: [] }])}>
              <Plus className="size-3" /> Add
            </Button>
          </span>
        </Tip>
      }
    >
      {!draft.filters.length && <p className="text-xs text-subtle">Date range and locations come from the dashboard.</p>}
      <ul className="space-y-2">
        {draft.filters.map((f, i) => {
          const def = catalog.dimensions.find((d) => d.ref === f.ref);
          const ops = OPS.filter((o) => o.types.includes(def?.type ?? "string"));
          const patch = (p: Partial<Filter>) => setFilters(draft.filters.map((x, j) => (j === i ? { ...x, ...p } : x)));
          const pickField = (ref: string) => {
            const d = catalog.dimensions.find((x) => x.ref === ref);
            patch({ ref, op: d?.type === "boolean" ? "eq" : "in", values: d?.type === "boolean" ? [true] : [] });
          };
          return (
            <li key={i} className="space-y-1.5 rounded-lg bg-surface-2 p-2">
              <div className="flex gap-1.5">
                <Select value={f.ref} onChange={(e) => pickField(e.target.value)} className="min-w-0 flex-1 text-xs" aria-label="Filter field">
                  {!f.ref && <option value="" disabled>Choose a field…</option>}
                  {def && !allowed.includes(def) && <option value={def.ref}>{labels.get(def.ref)}</option>}
                  {byModel.map(([model, ds]) => (
                    <optgroup key={model} label={model}>
                      {ds.map((d) => <option key={d.ref} value={d.ref}>{labels.get(d.ref)}</option>)}
                    </optgroup>
                  ))}
                </Select>
                {def && (
                  <Select value={f.op} onChange={(e) => patch({ op: e.target.value as FilterOp, values: [] })} className="text-xs" aria-label="Filter condition">
                    {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                  </Select>
                )}
                <button onClick={() => setFilters(draft.filters.filter((_, j) => j !== i))} className="px-1 text-subtle hover:text-foreground" aria-label="Remove filter">
                  <X className="size-3.5" />
                </button>
              </div>
              {def && <FilterValue key={`${f.ref}:${f.op}`} catalog={catalog} field={def} filter={f} onChange={(values) => patch({ values })} locationIds={locationIds} />}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** The value part of a filter, shaped by its condition: a pick list, free text, a number or yes/no. */
function FilterValue({ catalog, field, filter, onChange, locationIds }: {
  catalog: Catalog;
  field: CatalogDimension;
  filter: Filter;
  onChange: (values: Filter["values"]) => void;
  locationIds?: string[];
}) {
  switch (filter.op) {
    case "is_null":
    case "not_null":
      return null;
    case "in":
    case "not_in":
      return <FilterValues catalog={catalog} field={field} values={filter.values} onChange={onChange} locationIds={locationIds} />;
    case "eq":
      return (
        <Select value={String(filter.values[0] ?? true)} onChange={(e) => onChange([e.target.value === "true"])} className="h-7 w-full text-xs" aria-label="Value">
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      );
    default: {
      const isNumber = field.type === "number";
      return (
        <Input
          type={isNumber ? "number" : "text"}
          placeholder={isNumber ? "Number" : "Text"}
          className="h-7 text-xs"
          defaultValue={filter.values[0] === undefined ? "" : String(filter.values[0])}
          onBlur={(e) => {
            const v = e.target.value.trim();
            onChange(!v ? [] : isNumber ? (Number.isFinite(Number(v)) ? [Number(v)] : []) : [v]);
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        />
      );
    }
  }
}
